import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2 } from 'lucide-react';
import { CheckCircle, Link as LinkIcon } from 'lucide-react';

interface Platform {
  id: string;
  name: string;
  icon: React.ComponentType<any>;
  color: string;
  connected: boolean;
  inactive?: boolean;
}

interface SocialConnectionCardProps {
  platform: Platform;
  onConnect: () => void;
  onDisconnect?: () => void;
  isConnecting?: boolean;
}

export const SocialConnectionCard: React.FC<SocialConnectionCardProps> = ({ 
  platform, 
  onConnect, 
  onDisconnect,
  isConnecting = false 
}) => {
  const Icon = platform.icon;

  return (
    <Card className="relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-1 ${platform.color}`} />
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${platform.color} text-white`}>
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <CardTitle className="text-lg">{platform.name}</CardTitle>
              <CardDescription>Connect your {platform.name} account</CardDescription>
            </div>
          </div>
          {platform.connected && (
            <Badge variant="secondary" className="bg-green-100 text-green-700">
              <CheckCircle className="w-3 h-3 mr-1" />
              Connected
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {platform.connected ? (
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">
              Last sync: {new Date().toLocaleDateString()}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="flex-1">
                View Analytics
              </Button>
              {onDisconnect && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={onDisconnect}
                  className="flex-1"
                >
                  Disconnect
                </Button>
              )}
            </div>
          </div>
        ) : platform.inactive ? (
          <div className="space-y-2">
            <Button disabled className="w-full opacity-50 cursor-not-allowed">
              Inactive
            </Button>
            <p className="text-xs text-muted-foreground text-center">Coming Soon</p>
          </div>
        ) : (
          <Button onClick={onConnect} className="w-full" disabled={isConnecting}>
            {isConnecting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                <LinkIcon className="w-4 h-4 mr-2" />
                Connect {platform.name}
              </>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};